//@ts-ignore
import { createEffect, createSignal, onCleanup, onMount, Index } from "solid-js";
import { View, Text, hexColor, Show } from "@lightningtv/solid";
import { useNavigate } from "@solidjs/router";
import { setGlobalBackground } from "../../../state";
import { Row } from "@lightningtv/solid-ui";
import { LazyUp } from "@lightningtv/solid/primitives";
import LoadingIndicator from "../loading/loading";
import Header from "../../components/Header";
import { fontFamily, isRTL, t } from "../../translations/configuration";
import { IconButton } from "../../components/playlistComponents/TopRightHeaders";
import SmartSearchProvider from "../../api/providers/smartSearchProvider";
import KbPanel from "../../components/KbPanel";

const PLACEHOLDER_IMG = "https://assets.orbixplay.com/no_movie_image_placeholder.png";

const SmartSearchPage = () => {
    const [inputValue, setInputValue] = createSignal("");
    const [isInitializing, setIsInitializing] = createSignal(true);
    const [isSearching, setIsSearching] = createSignal(false);
    const [hasError, setHasError] = createSignal(false);
    const [searchResults, setSearchResults] = createSignal<any[]>([]);
    const [focusedItem, setFocusedItem] = createSignal<any>(null);
    const [failedImages, setFailedImages] = createSignal<Set<string>>(new Set());

    const [searchInfo, setSearchInfo] = createSignal({
        totalMovies: 0,
        categories: [],
        searchedCategories: 0,
        totalCategories: 0,
    });

    let kbPanelRef: { setFocus: () => void } | undefined;
    let settingsRef: any, viewRef: any, headerRef: any;
    let searchTimeout: ReturnType<typeof setTimeout> | null = null;

    const navigate = useNavigate();
    const smartSearch = SmartSearchProvider.getInstance();

    const handleImageError = (id: string) => {
        setFailedImages(prev => new Set([...prev, id]));
    };

    // ── Initialize ────────────────────────────────────────────────────────
    const initializeSearch = async () => {
        setIsInitializing(true);
        setHasError(false);
        try {
            const initResult = await smartSearch.initializeSearch();
            setSearchInfo({
                totalMovies: initResult.totalMovies,
                categories: initResult.categories,
                searchedCategories: 0,
                totalCategories: 0,
            });
            const initialResults = await smartSearch.smartSearch("");
            setSearchResults(initialResults.results);
            setIsInitializing(false);
            setTimeout(() => kbPanelRef?.setFocus(), 100);
        } catch (error) {
            console.error("Search initialization failed:", error);
            setHasError(true);
            setIsInitializing(false);
        }
    };

    // ── Search ────────────────────────────────────────────────────────────
    const performSearch = async (query: string) => {
        setIsSearching(true);
        try {
            const result = await smartSearch.smartSearch(query);
            setSearchResults(result.results);
            setSearchInfo(prev => ({
                ...prev,
                searchedCategories: result.searchedCategories,
                totalCategories: result.totalCategories,
            }));
        } catch (error) {
            console.error("Search error:", error);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    };

    // Debounced search
    createEffect(() => {
        const query = inputValue();
        if (searchTimeout) clearTimeout(searchTimeout);
        if (isInitializing()) return;
        searchTimeout = setTimeout(() => performSearch(query), 500);
    });

    onMount(() => {
        setGlobalBackground("#0A1C34");
        setInputValue("");
        initializeSearch();
    });

    onCleanup(() => {
        if (searchTimeout) clearTimeout(searchTimeout);
    });

    const handleKeypadInteraction = (callback: () => void) => {
        if (!isInitializing() && !hasError()) callback();
    };

    // ── RTL right-to-left row ─────────────────────────────────────────────
    const RTL_VISIBLE = 6;
    const RTL_STEP = 182; // 166 + 16
    const RTL_ITEM_W = 166;
    const RTL_PANEL_W = 1200;

    const [rtlViewStart, setRtlViewStart] = createSignal(0);
    const [rtlFocusIdx, setRtlFocusIdx] = createSignal(0);
    const rtlRefs: any[] = Array(RTL_VISIBLE).fill(null);

    createEffect(() => { searchResults(); setRtlViewStart(0); setRtlFocusIdx(0); });

    const rtlWindowItems = () => searchResults().slice(rtlViewStart(), rtlViewStart() + RTL_VISIBLE);
    const rtlX = (rel: number) => RTL_PANEL_W - RTL_ITEM_W - rel * RTL_STEP;

    const rtlMoveLeft = () => {
        if (isInitializing() || hasError()) return;
        if (rtlFocusIdx() < RTL_VISIBLE - 1) {
            const nf = rtlFocusIdx() + 1; setRtlFocusIdx(nf);
            setTimeout(() => rtlRefs[nf]?.setFocus(), 0);
        } else if (rtlViewStart() + RTL_VISIBLE < searchResults().length) {
            setRtlViewStart(s => s + 1);
            setTimeout(() => rtlRefs[RTL_VISIBLE - 1]?.setFocus(), 0);
        }
    };

    const rtlMoveRight = () => {
        if (isInitializing() || hasError()) return;
        if (rtlFocusIdx() > 0) {
            const nf = rtlFocusIdx() - 1; setRtlFocusIdx(nf);
            setTimeout(() => rtlRefs[nf]?.setFocus(), 0);
        } else if (rtlViewStart() > 0) {
            setRtlViewStart(s => s - 1);
            setTimeout(() => rtlRefs[0]?.setFocus(), 0);
        } else {
            kbPanelRef?.setFocus();
        }
    };

    // Result count label
    const resultCount = () =>
        inputValue().trim()
            ? searchResults().length
            : searchInfo().totalMovies;

    return (
        <View color={hexColor("#0d0f14")} width={1920} height={1080}>
            <View
                width={1920}
                height={1080}
                positionAbsolute={0}
                onEscape={() => { if (!isInitializing()) navigate(-1); }}
            >

                <Header screen={t("search")} ref={(el) => headerRef = el}
                    onDown={() => handleKeypadInteraction(() => kbPanelRef?.setFocus())}
                    onLeft={() => isRTL() ? settingsRef?.setFocus() : null}
                    onRight={() => isRTL() ? null : settingsRef?.setFocus()} />

                {/* ── Settings button top-right ── */}
                <View x={isRTL() ? 1 : 1700} y={50}>
                    <IconButton
                        onRef={(el) => { settingsRef = el; }}
                        x={100} y={0}
                        src="https://i.ibb.co/Jjp8bmn4/setting.png"
                        onEnter={() => handleKeypadInteraction(() => navigate("/userInfo"))}
                        onLeft={() => isRTL() ? null : headerRef?.setFocus()}
                        onRight={() => isRTL() ? headerRef?.setFocus() : null}
                        onDown={() => handleKeypadInteraction(() => kbPanelRef?.setFocus())}
                    />
                </View>

                {/* ── LEFT PANEL ── */}
                <View
                    x={isRTL() ? 1350 : 70} y={200}
                    width={500} height={760}
                    borderRadius={30}
                    color={hexColor("#12151c")}
                >
                    {/* Panel title */}
                    <Text
                        x={30} y={60}
                        style={{ fontSize: 30, color: hexColor("#f0f6fc"), fontWeight: "bold", fontFamily: fontFamily() }}
                    >
                        {t("search")}
                    </Text>

                    {/* Close X */}
                    <View
                        x={440} y={60}
                        width={40} height={40}
                        borderRadius={10}
                        color={hexColor("#202329")}
                        onEnter={() => navigate(-1)}
                    >
                        <Text x={13} y={8} style={{ fontSize: 25, color: hexColor("#f0f6fc"), fontWeight: "bold" }}>
                            x
                        </Text>
                    </View>

                    {/* Search input box */}
                    <View
                        x={30} y={120}
                        width={450} height={70}
                        borderRadius={10}
                        color={hexColor("#202329")}
                        style={{ border: { width: 1, color: hexColor("#2d3748") } }}
                    >
                        <View x={20} y={23} width={24} height={24} src="https://i.ibb.co/nNhBcw8M/search.png" color={hexColor("#4b5563")} />
                        <Text
                            x={60} y={27}
                            style={{ fontSize: 24, color: hexColor(inputValue() ? "#f0f6fc" : "#ffffff") }}
                        >
                            {inputValue() || t("searchForMovies") || "Search for movies ..."}
                        </Text>
                    </View>

                    <View x={30} y={200}>
                        <KbPanel
                            kbRef={(r) => { kbPanelRef = r; }}
                            value={inputValue}
                            fieldLabel={() => t("search")}
                            hidePreview={true}
                            onChar={(c) => setInputValue(v => v + c)}
                            onBackspace={() => setInputValue(v => v.slice(0, -1))}
                            onDone={() => handleKeypadInteraction(() => isRTL() ? rtlRefs[0]?.setFocus() : viewRef?.setFocus())}
                        />
                    </View>
                </View>

                {/* ── RIGHT PANEL ── */}
                <View x={isRTL() ? 70 : 650} y={270} width={1200} height={700}>

                    {/* Title + count badge */}
                    <View x={0} y={0} style={{ flexDirection: "row" }} gap={16}>
                        <Text style={{ fontSize: 36, color: hexColor("#f0f6fc"), fontWeight: "bold", fontFamily: fontFamily() }}>
                            {t("movies")}
                        </Text>
                        <Show when={!isInitializing() && resultCount() > 0}>
                            <View x={130} y={6} width={145} height={28} borderRadius={8} color={hexColor("#12151c")}>
                                {/* Searching spinner text */}
                                <Text x={10} y={6} style={{ fontSize: 18, color: hexColor(isSearching() ? "#60a5fa" : "#8b949e"), fontWeight: "bold" }}>
                                    {isSearching() ? "Searching..." : `${resultCount()} MOVIES`}
                                </Text>
                            </View>
                        </Show>
                    </View>

                    {/* Movie cards row */}
                    <Show when={!isInitializing() && !hasError()}>

                        {/* LTR */}
                        <Show when={!isRTL()}>
                            <View y={70} clipping>
                                <Show when={searchResults().length > 0}>
                                    {/* @ts-ignore */}
                                    <LazyUp
                                        component={Row}
                                        onLeft={() => handleKeypadInteraction(() => kbPanelRef?.setFocus())}
                                        ref={viewRef}
                                        scroll="always"
                                        direction={"row" as any}
                                        upCount={8}
                                        each={searchResults()}
                                        gap={16}
                                    >
                                        {(item: any) => (
                                            <View
                                                width={166} height={250}
                                                borderRadius={12}
                                                style={{
                                                    border: { width: 0, color: hexColor("#2563eb") },
                                                    focus: { border: { width: 3, color: hexColor("#2563eb") } },
                                                }}
                                                onUp={() => settingsRef?.setFocus()}
                                                onFocus={() => setFocusedItem(item())}
                                                onEnter={() => handleKeypadInteraction(() =>
                                                    navigate(`/entity/movie/${item().stream_id}/${item().category_id}`)
                                                )}
                                            >
                                                <View x={2} y={2} width={162} height={246} borderRadius={12}
                                                    src={failedImages().has(String(item().stream_id))
                                                        ? PLACEHOLDER_IMG
                                                        : (item().stream_icon || item().cover)?.replace?.("http://", "https://")
                                                        || PLACEHOLDER_IMG}
                                                    onError={() => handleImageError(String(item().stream_id))}
                                                />
                                            </View>
                                        )}
                                    </LazyUp>
                                </Show>
                            </View>
                        </Show>

                        {/* RTL */}
                        <Show when={isRTL()}>
                            <View y={70} width={1200} height={270} clipping>
                                <Index each={rtlWindowItems() as any[]}>
                                    {(item: any, relIdx) => (
                                        <View
                                            ref={(el: any) => { rtlRefs[relIdx] = el; }}
                                            x={rtlX(relIdx)} y={0}
                                            width={166} height={250}
                                            borderRadius={12}
                                            style={{
                                                border: { width: 0, color: hexColor("#2563eb") },
                                                focus: { border: { width: 3, color: hexColor("#2563eb") } },
                                            }}
                                            onLeft={rtlMoveLeft}
                                            onRight={rtlMoveRight}
                                            onUp={() => settingsRef?.setFocus()}
                                            onFocus={() => { setFocusedItem(item()); setRtlFocusIdx(relIdx); }}
                                            onEnter={() => handleKeypadInteraction(() =>
                                                navigate(`/entity/movie/${item().stream_id}/${item().category_id}`)
                                            )}
                                        >
                                            <View x={2} y={2} width={162} height={246} borderRadius={12}
                                                src={failedImages().has(String(item().stream_id))
                                                    ? PLACEHOLDER_IMG
                                                    : (item().stream_icon || item().cover)?.replace?.("http://", "https://")
                                                    || PLACEHOLDER_IMG}
                                                onError={() => handleImageError(String(item().stream_id))}
                                            />
                                        </View>
                                    )}
                                </Index>
                            </View>
                        </Show>

                        {/* No results */}
                        <Show when={!isSearching() && searchResults().length === 0 && inputValue().trim()}>
                            <Text x={200} y={170} fontSize={30} style={{ color: hexColor("#7d7e82"), fontFamily: fontFamily() }}>
                                {t("noMoviesFound") || `No movies found for "${inputValue()}"`}
                            </Text>
                        </Show>

                        {/* ── Focused movie detail card ── */}
                        <Show when={focusedItem()}>
                            <View
                                x={0} y={350}
                                width={1200} height={230}
                                borderRadius={16}
                                color={hexColor("#15171b")}
                            >
                                {/* Left blue accent bar */}
                                <View
                                    x={0} y={0}
                                    width={4} height={230}
                                    borderRadius={2}
                                    color={hexColor("#2563eb")}
                                />

                                <View x={28} y={18}>
                                    {/* Title */}
                                    <Text style={{ fontSize: 28, color: hexColor("#f0f6fc"), fontWeight: "bold", fontFamily: fontFamily() }}>
                                        {focusedItem()?.name || focusedItem()?.title}
                                    </Text>

                                    {/* Meta row: year • duration • rating • trailer badge */}
                                    <View x={0} y={44} style={{ flexDirection: "row" }} gap={14}>

                                        <Show when={focusedItem()?.releaseDate || focusedItem()?.release_date}>
                                            <Text style={{ fontSize: 21, color: hexColor("#9ca3af") }}>
                                                {(focusedItem()?.releaseDate || focusedItem()?.release_date)?.substring(0, 4)}
                                            </Text>
                                            <Text style={{ fontSize: 21, color: hexColor("#374151") }}>•</Text>
                                        </Show>

                                        <Show when={focusedItem()?.duration && focusedItem()?.duration !== "0"}>
                                            <Text style={{ fontSize: 21, color: hexColor("#9ca3af") }}>
                                                {focusedItem()?.duration} min
                                            </Text>
                                            <Text style={{ fontSize: 21, color: hexColor("#374151") }}>•</Text>
                                        </Show>

                                        <Show when={focusedItem()?.rating || focusedItem()?.stars}>
                                            <View width={82} height={28} borderRadius={6} color={hexColor("#2d2a1a")}>
                                                <View x={8} y={5} width={18} height={18} src="https://i.ibb.co/4gK6MnK/star-1.png" />
                                                <Text x={30} y={5} style={{ fontSize: 19, color: hexColor("#f5c518"), fontWeight: "bold" }}>
                                                    {Number(focusedItem()?.rating || focusedItem()?.stars).toFixed(1)}
                                                </Text>
                                            </View>
                                        </Show>

                                        <Show when={focusedItem()?.youtube_trailer || focusedItem()?.trailer}>
                                            <View width={90} height={28} borderRadius={6} color={hexColor("#7c3aed")}>
                                                <Text x={12} y={5} style={{ fontSize: 17, color: hexColor("#ffffff"), fontWeight: "bold" }}>
                                                    ▶ Trailer
                                                </Text>
                                            </View>
                                        </Show>
                                    </View>

                                    {/* Genre + director */}
                                    <View x={0} y={82} style={{ flexDirection: "row" }} gap={12}>
                                        <Show when={focusedItem()?.genre}>
                                            <View height={26} width={focusedItem()?.genre?.length * 10} borderRadius={5} color={hexColor("#1a2235")}>
                                                <Text x={10} y={4} style={{ fontSize: 18, color: hexColor("#60a5fa") }}>
                                                    {focusedItem()?.genre}
                                                </Text>
                                            </View>
                                        </Show>
                                        <Show when={focusedItem()?.director}>
                                            <Text y={30} style={{ fontSize: 18, color: hexColor("#4b5563") }}>·</Text>
                                            <Text y={30} style={{ fontSize: 18, color: hexColor("#6b7280") }}>
                                                {focusedItem()?.director}
                                            </Text>
                                        </Show>
                                        <Show when={focusedItem()?.cast}>
                                            <Text y={60} style={{ fontSize: 18, color: hexColor("#4b5563") }}>·</Text>
                                            <Text y={60} style={{ fontSize: 18, color: hexColor("#6b7280") }}>
                                                {focusedItem()?.cast?.split(",").slice(0, 2).join(", ")}
                                            </Text>
                                        </Show>
                                    </View>
                                </View>

                                {/* Press OK hint */}
                                <View x={24} y={180} style={{ flexDirection: "row" }} gap={10}>
                                    <View x={0} y={0} width={32} height={32} borderRadius={16} color={hexColor("#2563eb")}>
                                        <View x={2} y={2} width={30} height={30} src="https://i.ibb.co/WNPhwwTN/Arrow-Forward.png" />
                                    </View>
                                    <Text x={40} y={10} style={{ fontSize: 18, color: hexColor("#8b949e") }}>{t("pressOkToPlay")}</Text>
                                </View>
                            </View>
                        </Show>
                    </Show>
                </View>

                {/* Loading overlay */}
                <Show when={isInitializing()}>
                    <View width={1920} height={1080} color={hexColor("#000000")} alpha={0.7} positionAbsolute={0}>
                        <View x={960} y={500}>
                            <LoadingIndicator isCategoriesIndicator={false} />
                        </View>
                    </View>
                </Show>

                {/* Error overlay */}
                <Show when={hasError()}>
                    <View width={1920} height={1080} color={hexColor("#000000")} alpha={0.8} positionAbsolute={0}>
                        <View x={760} y={400}>
                            <Text fontSize={42} color={hexColor("#ffffff")} style={{ fontFamily: fontFamily() }}>
                                {t("thereIsProblem")}
                            </Text>
                            <Text x={50} y={60} fontSize={28} color={hexColor("#8b949e")} style={{ fontFamily: fontFamily() }}>
                                {t("tryAgainLater")}
                            </Text>
                        </View>
                    </View>
                </Show>

            </View>
        </View>
    );
};

export default SmartSearchPage;